// The journal's equity curve: cumulative net P&L by day, with the drawdown below the running peak
// shaded underneath.
import * as d3 from 'd3';
import { palette } from './palette';

export function drawEquity(svgEl: SVGSVGElement, points: { date: string; cum: number; drawdown: number }[], fmt: (v: number) => string) {
  const C = palette();
  const W = 900, H = 240, P = { x0: 56, x1: 890, y0: 12, y1: 210 };
  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();
  svg.attr('viewBox', `0 0 ${W} ${H}`);
  if (points.length === 0) {
    svg.append('text').text('Your equity curve appears after the first closed trade').attr('x', W / 2).attr('y', H / 2).attr('text-anchor', 'middle').attr('class', 'dt-axis');
    return;
  }
  const pts = points.length === 1 ? [{ date: '', cum: 0, drawdown: 0 }, ...points] : points;
  const lo = Math.min(0, ...pts.map((p) => Math.min(p.cum, p.drawdown))), hi = Math.max(0, ...pts.map((p) => p.cum));
  const pad = (hi - lo) * 0.08 || 1;
  const x = d3.scaleLinear([0, pts.length - 1], [P.x0, P.x1]);
  const y = d3.scaleLinear([lo - pad, hi + pad], [P.y1, P.y0]);
  y.ticks(5).forEach((v) => {
    svg.append('line').attr('x1', P.x0).attr('x2', P.x1).attr('y1', y(v)).attr('y2', y(v)).attr('stroke', v === 0 ? C.line : C.gridSoft);
    svg.append('text').text(fmt(v)).attr('x', P.x0 - 8).attr('y', y(v) + 3).attr('text-anchor', 'end').attr('class', 'dt-axis');
  });
  const step = Math.max(1, Math.ceil(pts.length / 8));
  pts.forEach((p, i) => {
    if (!p.date || (i % step && i !== pts.length - 1)) return;
    const d = new Date(p.date + 'T00:00:00');
    svg.append('text').text(d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })).attr('x', x(i)).attr('y', H - 8).attr('text-anchor', 'middle').attr('class', 'dt-axis');
  });
  // Drawdown: from zero down to the drawdown at each day.
  svg.append('path').attr('d', d3.area<(typeof pts)[number]>().x((_p, i) => x(i)).y0(y(0)).y1((p) => y(p.drawdown)).curve(d3.curveMonotoneX)(pts))
    .attr('fill', C.r).attr('opacity', 0.16);
  const up = pts[pts.length - 1].cum >= 0;
  const id = `eq-grad-${Math.random().toString(36).slice(2)}`;
  const g = svg.append('defs').append('linearGradient').attr('id', id).attr('x1', 0).attr('x2', 0).attr('y1', 0).attr('y2', 1);
  g.append('stop').attr('offset', 0).attr('stop-color', up ? C.g : C.r).attr('stop-opacity', 0.28);
  g.append('stop').attr('offset', 1).attr('stop-color', up ? C.g : C.r).attr('stop-opacity', 0);
  svg.append('path').attr('d', d3.area<(typeof pts)[number]>().x((_p, i) => x(i)).y0(y(0)).y1((p) => y(p.cum)).curve(d3.curveMonotoneX)(pts)).attr('fill', `url(#${id})`);
  svg.append('path').attr('d', d3.line<(typeof pts)[number]>().x((_p, i) => x(i)).y((p) => y(p.cum)).curve(d3.curveMonotoneX)(pts))
    .attr('fill', 'none').attr('stroke', up ? C.gd : C.rd).attr('stroke-width', 2);
  const last = pts[pts.length - 1];
  svg.append('circle').attr('cx', x(pts.length - 1)).attr('cy', y(last.cum)).attr('r', 4).attr('fill', up ? C.gd : C.rd);
}
